import type { CompRef } from '@wix/document-services-types';
import type {
  Behaviors,
  EditorElement,
  InlineElement,
} from '@wix/component-protocol/schema';
import { DATA, CONTAINER_TYPE } from '@wix/component-protocol/schema';
import { SYSTEM_CONTAINER_TYPE } from '../constants/componentTypes';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import type { SimpleContainer } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { ContainerPlatformService } from './ContainerPlatformService';
import { MENU_ITEMS_DEFAULT_SIMPLE_CONTAINER } from '../constants/containerDefaults';
import { isTPAPage } from './tpaUtils';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class BehaviorsPlatformService extends AbstractComponentsPlatformService {
  private readonly containerService: ContainerPlatformService;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.containerService = new ContainerPlatformService(...args);
  }
  private isInnerElement(
    manifest: EditorElement | InlineElement,
  ): manifest is InlineElement {
    return (manifest as InlineElement).behaviors !== undefined;
  }

  private isTopLevelElement(editorPointer: EditorPointer<CompRef>) {
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer);

    return !innerElementPath || innerElementPath.length === 0;
  }

  private getDataDefinitions(editorPointer: EditorPointer<CompRef>) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI()
      .resolver.getDataDefinitions();
  }

  private isTemplateContainer(editorPointer: EditorPointer<CompRef>): boolean {
    const dataDefinitions = this.getDataDefinitions(editorPointer);
    if (!dataDefinitions) {
      return false;
    }

    return Object.values(dataDefinitions).some(
      (item) =>
        item?.dataType === DATA.DATA_TYPE.container &&
        item?.container?.containerType === CONTAINER_TYPE.template,
    );
  }

  private getInternalContainerBehavior(
    editorPointer: EditorPointer<CompRef>,
  ): SimpleContainer | null {
    if (this.containerService.isMenuItemsContainer(editorPointer)) {
      return MENU_ITEMS_DEFAULT_SIMPLE_CONTAINER;
    }

    const dataDefinitions = this.getDataDefinitions(editorPointer);

    if (!dataDefinitions) {
      return null;
    }

    const filteredContainerDataEntries = Object.entries(dataDefinitions).filter(
      ([, item]) =>
        item?.dataType === DATA.DATA_TYPE.container &&
        item?.container?.containerType === CONTAINER_TYPE.simple,
    );
    if (filteredContainerDataEntries.length !== 1) {
      return null;
    }
    const [, containerDefinition] = filteredContainerDataEntries[0];
    return containerDefinition.container?.simple ?? null;
  }

  getValue(
    editorPointer: EditorPointer<CompRef>,
    behaviorName: keyof Behaviors,
  ) {
    /**
     * this a is proprietary internal container behavior
     * if 'behaviors' could be attached to the root element, we could awoid that conditional flow
     * TODO talk with architects
     * */

    const componentType = this.dm.components.getType(
      editorPointer.documentPointer,
    );

    if (
      behaviorName !== 'removable' &&
      componentType === SYSTEM_CONTAINER_TYPE
    ) {
      const internalContainerBehavior =
        this.getInternalContainerBehavior(editorPointer);
      if (internalContainerBehavior) {
        return internalContainerBehavior?.behaviors?.[behaviorName] ?? null;
      }
      if (this.isTemplateContainer(editorPointer)) {
        return true;
      }
    }

    if (
      behaviorName === 'removable' &&
      this.containerService.isPrivateSlotPlugin(editorPointer)
    ) {
      return false;
    }

    if (behaviorName === 'removable' && isTPAPage(this.dm, componentType)) {
      return false;
    }

    /**
     * note, we are not resolving behavior for a virtual element
     * since there is no way to merge behavior for a virtual element at the moment
     * this should work only for the single pointer that points to a real element
     * there shouldn't be the case when we check behavior for multiple components
     * */
    const manifest = this.manifestStore.getResolvedEditorElement(editorPointer);

    // This is a temporary solution to handle top level elements until the manifest does not have the default behaviors
    // TODO: remove this once the manifest has the default behaviors for top level elements
    if (
      this.isTopLevelElement(editorPointer) &&
      (behaviorName === 'selectable' || behaviorName === 'removable')
    ) {
      return true;
    }

    if (!manifest || !this.isInnerElement(manifest)) {
      return null;
    }

    return (manifest.behaviors?.[behaviorName] as boolean) ?? null;
  }
}
